import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { locationsAPI } from '../services/api';
import HierarchicalQRWizard from '../components/HierarchicalQRWizard';
import './QRCodePage.css';

interface QRLocationData {
  id?: number;
  location: string;
  url: string;
  qrCode: string;
  isCustom?: boolean;
  locationType?: string;
  parentId?: number;
  floorNumber?: number;
  roomNumber?: string;
}

const QRCodePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'wizard' | 'all' | 'legacy'>('wizard');
  const [allQRs, setAllQRs] = useState<QRLocationData[]>([]);
  const [customLocation, setCustomLocation] = useState('');
  const [generatedQRs, setGeneratedQRs] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(true);

  // QR kod URL'sini oluştur
  const generateQRCodeUrl = (locationName: string) => {
    const encodedLocation = encodeURIComponent(locationName);
    return `${window.location.origin}/feedback-form?location=${encodedLocation}&source=qr`;
  };

  // QR kodunu canvas'ta oluştur
  const generateQRCodeCanvas = async (url: string, location: string) => {
    try {
      const canvas = document.getElementById(`qr-${location.replace(/\s+/g, '-')}`) as HTMLCanvasElement;
      if (canvas) {
        await QRCode.toCanvas(canvas, url, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });

        const dataUrl = canvas.toDataURL();
        setGeneratedQRs(prev => ({
          ...prev,
          [location]: dataUrl
        }));
      }
    } catch (error) {
      console.error('QR kod oluşturma hatası:', error);
    }
  };

  // QR kodunu indir
  const downloadQRCode = (location: string, qrCode: string) => {
    const dataUrl = generatedQRs[location];
    if (dataUrl) {
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `QR-${location.replace(/\s+/g, '-')}-${qrCode}.png`;
      link.click();
    }
  };

  // QR kodunu yazdır
  const printQRCode = (location: string) => {
    const dataUrl = generatedQRs[location];
    if (dataUrl) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head><title>QR Kod - ${location}</title></head>
            <body style="text-align: center; padding: 20px;">
              <h2>${location}</h2>
              <img src="${dataUrl}" style="max-width: 300px;" />
              <p>QR Kod: ${allQRs.find(qr => qr.location === location)?.qrCode || ''}</p>
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  // URL'yi panoya kopyala
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('URL panoya kopyalandı!');
    }).catch(err => {
      console.error('Kopyalama hatası:', err);
    });
  };

  // Custom QR lokasyonu ekle
  const addCustomLocation = async () => {
    if (customLocation.trim()) {
      try {
        const response = await locationsAPI.createCustomQR(customLocation.trim());
        const newQR: QRLocationData = {
          id: response.data.id,
          location: response.data.location,
          url: response.data.url,
          qrCode: response.data.qrCode,
          isCustom: true
        };

        setAllQRs(prev => [...prev, newQR]);
        setCustomLocation('');

        setTimeout(() => {
          generateQRCodeCanvas(newQR.url, newQR.location);
        }, 100);

        alert('Özel QR kod başarıyla oluşturuldu!');
      } catch (error: any) {
        console.error('Custom QR oluşturma hatası:', error);
        alert(error.response?.data?.message || 'QR kod oluşturulamadı!');
      }
    }
  };

  // Custom QR lokasyonunu sil
  const removeCustomLocation = async (location: string) => {
    const qrToRemove = allQRs.find(qr => qr.location === location && qr.isCustom);

    if (qrToRemove && qrToRemove.id) {
      try {
        await locationsAPI.deleteCustomQR(qrToRemove.id);

        setAllQRs(prev => prev.filter(qr => qr.location !== location));
        setGeneratedQRs(prev => {
          const newQRs = { ...prev };
          delete newQRs[location];
          return newQRs;
        });

        alert('Özel QR kod başarıyla silindi!');
      } catch (error: any) {
        console.error('Custom QR silme hatası:', error);
        alert(error.response?.data?.message || 'QR kod silinemedi!');
      }
    }
  };

  // Backend'den tüm lokasyonları getir
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoading(true);
        const response = await locationsAPI.getLocationsForQR();
        const backendLocations = response.data;

        const formattedLocations: QRLocationData[] = backendLocations.map((loc: any) => ({
          id: loc.id,
          location: loc.location,
          url: loc.url,
          qrCode: loc.qrCode,
          isCustom: loc.isCustom || false,
          locationType: loc.locationType,
          parentId: loc.parentId,
          floorNumber: loc.floorNumber,
          roomNumber: loc.roomNumber
        }));

        setAllQRs(formattedLocations);
      } catch (error) {
        console.error('Lokasyonlar yüklenemedi:', error);
        setAllQRs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, []);

  // QR kodlarını otomatik oluştur
  useEffect(() => {
    if (!loading && allQRs.length > 0) {
      allQRs.forEach(item => {
        setTimeout(() => {
          generateQRCodeCanvas(item.url, item.location);
        }, 100);
      });
    }
  }, [loading, allQRs]);

  if (loading) {
    return (
      <div className="qr-code-container">
        <div className="qr-loading">Lokasyonlar yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="qr-code-container">
      <div className="qr-code-header">
        <h1>QR Kod Yönetimi</h1>
        <p>Hastane lokasyonları için QR kodları oluşturun ve yönetin</p>
      </div>

      {/* Tab Navigation */}
      {/* <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'wizard' ? 'active' : ''}`}
          onClick={() => setActiveTab('wizard')}
        >
          🎯 Hiyerarşik QR Oluştur
        </button>
        <button
          className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          📋 Tüm QR Kodları
        </button>
        <button
          className={`tab-button ${activeTab === 'legacy' ? 'active' : ''}`}
          onClick={() => setActiveTab('legacy')}
        >
          ➕ Özel QR Oluştur
        </button>
      </div> */}

      {/* Tab Content */}
      <div className="tab-content">
        {/* Wizard Tab */}
        {activeTab === 'wizard' && (
          <div className="tab-panel">
            <HierarchicalQRWizard />
          </div>
        )}

        {/* All QR Codes Tab */}
        {activeTab === 'all' && (
          <div className="tab-panel">
            <div className="qr-codes-grid">
              {allQRs.map((item, index) => (
                <div key={`${item.location}-${index}`} className="qr-code-card">
                  <div className="qr-code-info">
                    <h3>{item.location}</h3>
                    <p className="qr-code-id">QR ID: {item.qrCode}</p>
                    {item.isCustom && (
                      <span className="custom-badge">Özel</span>
                    )}
                    {item.locationType && typeof item.locationType === 'string' && (
                      <span className={`type-badge ${item.locationType.toLowerCase()}`}>
                        {item.locationType === 'Department' && '🏥 Departman'}
                        {item.locationType === 'Floor' && '🏢 Kat'}
                        {item.locationType === 'Room' && '🚪 Oda'}
                      </span>
                    )}
                  </div>

                  <div className="qr-code-visual">
                    <canvas
                      id={`qr-${item.location.replace(/\s+/g, '-')}`}
                      className="qr-canvas"
                    ></canvas>
                  </div>

                  <div className="qr-code-actions">
                    <div className="qr-url">
                      <strong>URL:</strong>
                      <span className="url-text">{item.url}</span>
                      <button
                        onClick={() => copyToClipboard(item.url)}
                        className="copy-button"
                        title="URL'yi kopyala"
                      >
                        📋
                      </button>
                    </div>

                    <div className="action-buttons">
                      <button
                        onClick={() => downloadQRCode(item.location, item.qrCode)}
                        className="download-button"
                        disabled={!generatedQRs[item.location]}
                      >
                        ⬇️ İndir
                      </button>
                      <button
                        onClick={() => printQRCode(item.location)}
                        className="print-button"
                        disabled={!generatedQRs[item.location]}
                      >
                        🖨️ Yazdır
                      </button>
                      {item.isCustom && (
                        <button
                          onClick={() => removeCustomLocation(item.location)}
                          className="remove-button"
                        >
                          🗑️ Sil
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {allQRs.length === 0 && (
              <div className="no-qr-codes">
                <p>Henüz QR kod bulunmuyor. Hiyerarşik QR oluştur sekmesinden yeni QR kodları oluşturabilirsiniz.</p>
              </div>
            )}
          </div>
        )}

        {/* Legacy Custom QR Tab */}
        {activeTab === 'legacy' && (
          <div className="tab-panel">
            <div className="custom-qr-section">
              <h2>Özel QR Kod Oluştur</h2>
              <p className="section-description">
                Hiyerarşik yapıya uymayan özel lokasyonlar için QR kod oluşturun
              </p>
              <div className="custom-qr-form">
                <input
                  type="text"
                  value={customLocation}
                  onChange={(e) => setCustomLocation(e.target.value)}
                  placeholder="Lokasyon adı girin (örn: Acil Servis)"
                  className="custom-location-input"
                  onKeyPress={(e) => e.key === 'Enter' && addCustomLocation()}
                />
                <button
                  onClick={addCustomLocation}
                  className="add-location-button"
                  disabled={!customLocation.trim()}
                >
                  ➕ QR Kod Oluştur
                </button>
              </div>
            </div>

            <div className="qr-instructions">
              <h2>QR Kod Kullanım Talimatları</h2>
              <ol>
                <li>Yukarıdaki QR kodlarını indirin veya yazdırın</li>
                <li>Her lokasyon için QR kodu yazdırın</li>
                <li>QR kodları ilgili alanlara yerleştirin</li>
                <li>Hastalar QR kodu taradığında otomatik olarak lokasyon bilgisi dolu form açılacak</li>
                <li>Geri bildirim QR kaynaklı olarak işaretlenecek</li>
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QRCodePage;